import { useImpersonation } from "@/contexts/ImpersonationContext";
import { hasMinRole } from "@/lib/constants";
import { AccessGroupsCard } from "./AccessGroupsCard";
import { FolderAccessCard } from "./FolderAccessCard";

/**
 * VIB-2726: administrasjon av grupper og mappetilganger.
 *
 * ÉN komponent, to innganger (Erik 02.10): den rendres både under
 * Innstillinger → Tilganger og fra arkivinnstillingene. Derfor tar den imot en
 * mappe å starte på i stedet for å finne den selv — alt annet er identisk,
 * og det er hele poenget med at det er samme komponent.
 *
 * Gruppekortet er bevisst DOMENENØYTRALT: en gruppe er en samling mennesker,
 * og tabellen heter `access_groups` nettopp fordi grupper kan tas i bruk utenfor
 * arkivet senere. Mappetildelingen er den arkivspesifikke delen, og ligger for
 * seg i `FolderAccessCard`.
 */
export function AccessAdminPanel({
  initialFolderId,
}: {
  initialFolderId?: string | null;
}) {
  const { activeUser, isImpersonating } = useImpersonation();
  const kanAdministrere = hasMinRole(activeUser?.role ?? "user", "admin");

  if (!kanAdministrere) {
    return <p className="text-sm text-muted-foreground">Du har ikke tilgang.</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        En gruppe er en navngitt samling mennesker. Rollen sier hvor høyt noen
        rekker, gruppa sier hvem — og tilgang til en mappe er det ene{" "}
        <span className="font-medium">eller</span> det andre. Et styremedlem er
        ikke en rang over en produksjonsleder, det er en annen person, og det er
        grupper som uttrykker det.
      </p>

      {isImpersonating && (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          Du ser systemet som {activeUser?.full_name}. Endringer du gjør her
          logges på deg selv, ikke på hen.
        </p>
      )}

      <AccessGroupsCard />
      <FolderAccessCard initialFolderId={initialFolderId ?? null} />
    </div>
  );
}
